import { z } from "zod";
import { ClientHoldingPatternSchema, ClientStatusSchema } from "./client";
import { investorBankDetailsSchema } from "./investor/investor";
import { productTypeSchema } from "./portfolio";

// Shared identity fields for a client that belongs to a family, common to
// both the partner-facing family detail response and the investor-facing
// roster response.
export const FamilyMemberBaseSchema = z.object({
  client_id: z.number(),
  investor_id: z.number(),
  family_member_id: z.number(),
  name: z.string(),
  pan: z.string(),
  type: z.string(),
  phone_number: z.string(),
  status: ClientStatusSchema,
  access_role: z.string(),
  removed_at: z.string().nullable(),
  investment_account_id: z.string(),
});

export const FamilyDetailMemberSchema = FamilyMemberBaseSchema.extend({
  investment_account_id: z.string().nullable().optional(),
  type: z.string().optional(),
  removed_at: z.string().nullable().optional(),
  display_name: z.string().optional(),
  primary_bank_account: investorBankDetailsSchema.optional(),
  holding_pattern: ClientHoldingPatternSchema,
});

export const FamilySourceSchema = z.enum(["INTERNAL", "EXTERNAL", "OVERALL"]);

export const FamilyAumSummarySchema = z.object({
  returns: z.number().optional(),
  current_value_pct: z.number().optional(),
  current_value: z.number(),
  invested_value: z.number(),
  previous_day_value: z.number(),
  xirr: z.number().nullable().optional(),
  blended_xirr: z.number().nullable(),
  lifetime_xirr: z.number().nullable().optional(),
  active_xirr: z.number().nullable().optional(),
});

export const FamilyAumMemberSchema = z.object({
  investor_id: z.number(),
  client_id: z.number(),
  investment_account_id: z.string(),
  display_name: z.string(),
  member_first_name: z.string(),
  current_value: z.number(),
  invested_value: z.number(),
  previous_day_value: z.number(),
  share_pct: z.number(),
  blended_xirr: z.number().nullable(),
  lifetime_xirr: z.number().nullable().optional(),
  active_xirr: z.number().nullable().optional(),
  status: ClientStatusSchema,
  holding_pattern: ClientHoldingPatternSchema,
});

export const FamilyAumOverviewSchema = z.object({
  source: FamilySourceSchema,
  summary: FamilyAumSummarySchema,
  members: z.array(FamilyAumMemberSchema),
});

export const FamilyMonthlySipSummarySchema = z.object({
  monthly_sip: z.number(),
  cumulative_invested: z.number(),
  active_sip_count: z.number(),
});

export const FamilyMonthlySipMemberSchema = z.object({
  investor_id: z.number(),
  client_id: z.number(),
  investment_account_id: z.string(),
  display_name: z.string(),
  member_first_name: z.string(),
  monthly_sip: z.number(),
  cumulative_invested: z.number(),
  active_sip_count: z.number(),
  holding_pattern: ClientHoldingPatternSchema,
});

export const FamilyMonthlySipOverviewSchema = z.object({
  summary: FamilyMonthlySipSummarySchema,
  members: z.array(FamilyMonthlySipMemberSchema),
});

// One of a person's own accounts, nested under their roster row — e.g. an
// individual account plus a linked HUF account. Structural/identity only —
// money values live on family/context, not family/roster.
export const FamilyRosterClientSchema = z.object({
  client_id: z.number(),
  investment_account_id: z.string(),
  investment_account_guid: z.string(),
  name: z.string(),
  first_name: z.string(),
  type: z.string(),
  pan: z.string(),
  phone_number: z.string(),
  holding_pattern: ClientHoldingPatternSchema,
});

// A person in the family, with all of their accounts nested under `clients`.
export const FamilyRosterMemberSchema = z.object({
  family_member_id: z.number(),
  investor_id: z.number(),
  display_name: z.string(),
  first_name: z.string(),
  access_role: z.string(),
  is_self: z.boolean(),
  status: ClientStatusSchema,
  phone_number: z.string(),
  clients: z.array(FamilyRosterClientSchema),
});

export const FamilyRosterResponseSchema = z.object({
  family_id: z.number().nullable(),
  family_name: z.string().nullable(),
  results: z.array(FamilyRosterMemberSchema),
});

// Investor-only bootstrap: which family state the caller is in, plus the
// ready-to-render account-switcher payload.
export const FamilyContextStateSchema = z.enum([
  "FAMILY_FULL",
  "FAMILY_SELF",
  "CONSOLIDATED",
  "SOLO",
]);

export const FamilyContextAccessRoleSchema = z
  .enum(["FULL", "SELF"])
  .nullable();

export const FamilyContextFamilySchema = z.object({
  family_id: z.number(),
  family_name: z.string(),
  member_count: z.number(),
  visible_member_count: z.number(),
  family_feature_viewed: z.boolean(),
});

export const FamilyContextFamilyAccountSchema = z.object({
  kind: z.literal("family"),
  label: z.string(),
  current_value: z.string(),
  partial: z.boolean(),
});

export const FamilyContextClientAccountSchema = z.object({
  kind: z.literal("client"),
  client_id: z.number(),
  investment_account_id: z.string(),
  investment_account_guid: z.string(),
  name: z.string(),
  first_name: z.string(),
  status: ClientStatusSchema,
  type: z.string(),
  is_own: z.boolean(),
  current_value: z.string(),
  invested_value: z.string(),
  total_return: z.string(),
  absolute_return_pct: z.number().nullable(),
  holding_pattern: ClientHoldingPatternSchema,
});

export const FamilyContextAccountSchema = z.discriminatedUnion("kind", [
  FamilyContextFamilyAccountSchema,
  FamilyContextClientAccountSchema,
]);

export const FamilyContextResponseSchema = z.object({
  state: FamilyContextStateSchema,
  access_role: FamilyContextAccessRoleSchema,
  family: FamilyContextFamilySchema.nullable(),
  accounts: z.array(FamilyContextAccountSchema),
});

export const FamilyGoalsSummaryEtaSchema = z.object({
  years: z.number(),
  months: z.number(),
});

export const FamilyGoalsSummaryGoalSchema = z.object({
  goal_id: z.number(),
  client_id: z.number(),
  name: z.string(),
  category: z.string(),
  status: z.string(),
  current_value: z.number(),
  target_corpus: z.number(),
  progress_pct: z.number(),
  eta: FamilyGoalsSummaryEtaSchema.nullable(),
  duration_months: z.number(),
  future_value: z.string(),
});

export const FamilyGoalsSummaryMemberSchema = z.object({
  client_id: z.number(),
  investment_account_id: z.string(),
  investor_id: z.number(),
  display_name: z.string(),
  goal_count: z.number(),
  total_target_corpus: z.number(),
  total_current_value: z.number(),
  average_completion: z.number(),
  goals: z.array(FamilyGoalsSummaryGoalSchema),
  holding_pattern: ClientHoldingPatternSchema,
});

export const FamilyGoalsSummarySummarySchema = z.object({
  total_goal_value: z.number(),
  goal_count: z.number(),
  average_completion: z.number(),
});

export const FamilyGoalsSummaryOverviewSchema = z.object({
  summary: FamilyGoalsSummarySummarySchema,
  members: z.array(FamilyGoalsSummaryMemberSchema),
});

export const FamilySchema = z.object({
  id: z.number(),
  name: z.string(),
  remark: z.string().nullable().optional(),
  distributor: z.number().optional(),
  created_at: z.string().optional(),
  modified_at: z.string().optional(),
  member_count: z.number().optional(),
  member_first_names: z.array(z.string()).optional(),
  member_names: z.array(z.string()).optional(),
  total_aum: z.number().optional(),
  total_sip_book: z.number().optional(),
  primary_contact: z.string().optional(),
  last_seen: z.string().nullable().optional(),
  employee_id: z.number().nullable().optional(),
  employee_name: z.string().nullable().optional(),
  family_status: z.string().optional(),
  badge: z.enum(["new_member", "invite_pending"]).optional(),
});

export const FamilyDetailSchema = FamilySchema.extend({
  members: z.array(FamilyDetailMemberSchema).optional(),
});

export const FamiliesFacetsSchema = z.object({
  member_buckets: z.record(z.string(), z.number()).optional(),
  assigned_to: z
    .array(
      z.object({
        employee_id: z.number(),
        employee_name: z.string(),
        count: z.number(),
      })
    )
    .optional(),
  unassigned_count: z.number().optional(),
});

export const FamiliesResponseSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(FamilySchema),
  facets: FamiliesFacetsSchema.optional(),
});

export const FamilyAccessRoleSchema = z.enum(["FULL", "SELF"]);

export const CreateFamilyMemberPayloadSchema = z.object({
  client_id: z.number(),
  access_role: FamilyAccessRoleSchema,
});

export const CreateFamilyPayloadSchema = z.object({
  name: z.string(),
  remark: z.string().optional(),
  assigned_to: z.number().optional(),
  confirm_move: z.boolean(),
  members: z.array(CreateFamilyMemberPayloadSchema).min(1),
});

export const UpdateFamilyAddMemberPayloadSchema = z.object({
  client_id: z.number(),
  access_role: FamilyAccessRoleSchema.optional(),
});

export const UpdateFamilySetRolePayloadSchema = z.object({
  member_id: z.number(),
  access_role: FamilyAccessRoleSchema,
});

export const UpdateFamilyPayloadSchema = z.object({
  name: z.string().optional(),
  remark: z.string().nullable().optional(),
  assigned_to: z.number().nullable().optional(),
  confirm_move: z.boolean().optional(),
  add_members: z.array(UpdateFamilyAddMemberPayloadSchema).optional(),
  remove_members: z.array(z.number()).optional(),
  set_roles: z.array(UpdateFamilySetRolePayloadSchema).optional(),
});

export const BulkAssignFamiliesPayloadSchema = z.object({
  employee_id: z.number().nullable(),
  family_ids: z.array(z.number()).min(1),
});

export const BulkAssignFamiliesResponseSchema = z.object({
  assigned_count: z.number(),
  employee_id: z.number().nullable(),
});

// One row per scheme, aggregated across every member of the family. Money
// fields (nav, invested_value, current_value, gain) come back as exact
// decimal strings; units/returns/xirr are plain numbers.
export const FamilySchemeWiseHoldingSchema = z.object({
  scheme_name: z.string(),
  product_type: productTypeSchema,
  internal_flag: z.boolean(),
  units: z.number(),
  nav: z.string(),
  invested_value: z.string(),
  current_value: z.string(),
  gain: z.string(),
  absolute_return_pct: z.number(),
  xirr: z.number(),
});
